import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { motion } from "framer-motion";
import styles from "./FilterButton.module.css";
import PropTypes from "prop-types";

import path from "./path.svg";
import {
  findCategoryRecord,
  getCategoryColorStyle,
} from "./helpers/categoryColor";
import { groupByRowNumber } from "./helpers/rowGrouping";

export function buildCategoriesFromAPI(apiCategories = []) {
  if (!Array.isArray(apiCategories) || apiCategories.length === 0) {
    // Favorites isn't tied to the API-provided category tree, so keep it
    // available even when there are no real categories to filter by.
    return { Favorites: [] };
  }

  const topLevel = apiCategories.filter((cat) => !cat.parentCategoryId);

  // Order by stepNumber - the API doesn't return a sortOrder field for
  // categories, so stepNumber is the actual ordering signal from the server.
  topLevel.sort((a, b) => (a.stepNumber || 0) - (b.stepNumber || 0));

  // Build the categories structure
  const result = {};

  topLevel.forEach((topCat) => {
    const topName = topCat.title;

    // Find all direct children, ordered by stepNumber
    const children = apiCategories
      .filter((cat) => cat.parentCategoryId === topCat.categoryId)
      .sort((a, b) => (a.stepNumber || 0) - (b.stepNumber || 0));

    if (children.length === 0) {
      // No children, just an array (for compatibility)
      result[topName] = [];
    } else {
      // Has children, build nested structure. Each child keeps its own
      // rowNumber (used to group middle-level pills into the rows the API
      // intends) alongside its grandchildren, which likewise keep their own
      // rowNumber (e.g. Literature > Books > Read/Unread sit on a separate
      // row from Business/Kids/Leadership/Motivational).
      const nested = {};
      children.forEach((child) => {
        const childName = child.title;

        const grandchildren = apiCategories
          .filter((cat) => cat.parentCategoryId === child.categoryId)
          .sort((a, b) => (a.stepNumber || 0) - (b.stepNumber || 0));

        nested[childName] = {
          rowNumber: child.rowNumber || 1,
          items: grandchildren.map((gc) => ({
            title: gc.title,
            rowNumber: gc.rowNumber || 1,
          })),
        };
      });

      result[topName] = nested;
    }
  });

  // Add Favorites with top-level category names
  result.Favorites = topLevel.map((cat) => cat.title);

  return result;
}

/* Only the specific pill whose own text genuinely overflows its own box
   should shrink - not a class of "long labels," not neighbors in the same
   row. clamp()/cqw can only react to the pill's rendered width, never to
   how many characters need to fit in it, so it can't target individual
   overflowing labels; this measures each label's own scrollWidth against
   its own pill's available width and nudges the font-size down only when
   it's actually needed.

   Layout settles across more than one animation frame (framer-motion layout
   animations, a sibling flex-wrapping when another pill is selected, etc.),
   so measuring on mount/resize with a single check can catch a transient,
   not-yet-final box size and shrink a pill that isn't really overflowing.
   Two chained rAFs let the browser commit the final layout before
   measuring, and a small pixel tolerance absorbs normal sub-pixel rounding
   so borderline-fitting labels aren't shrunk. The floor caps this at a
   "slight shrinkage," not a driving-to-fit-anything scale.

   Shared by every pill level (top-level/Favorites categoryPill labels and
   subCategory labels) so shrinking pill padding/basis to fit 4-per-row never
   trades "wraps to 3 rows" for "text silently overflows/clips" on a long
   category name. */
function useFitLabel(text) {
  const spanRef = useRef(null);

  const fit = useCallback(() => {
    const el = spanRef.current;
    const pill = el?.parentElement;
    if (!el || !pill) return;

    // Reset first so we always measure against the natural, CSS-driven
    // size - otherwise a previous shrink would compound on itself.
    el.style.fontSize = "";

    // Small enough to only absorb sub-pixel rounding, not to let a label
    // sit visibly flush against its pill's edge before shrinking kicks in.
    // 45px works for typical mobile widths, but on very narrow viewports
    // (e.g. 315px) that same tolerance is large relative to the pill's
    // available width, letting real overflow slip through unshrunk - so
    // it's reduced below the NARROW_VIEWPORT_PX breakpoint.
    const NARROW_VIEWPORT_PX = 320;
    const TOLERANCE_PX = window.innerWidth <= NARROW_VIEWPORT_PX ? 15 : 50;
    const pillStyle = window.getComputedStyle(pill);
    const available =
      pill.clientWidth -
      (parseFloat(pillStyle.paddingLeft) || 0) -
      (parseFloat(pillStyle.paddingRight) || 0);
    if (available <= 0) return;

    const overflow = el.scrollWidth - available;
    if (overflow <= TOLERANCE_PX) return;

    const baseSize = parseFloat(window.getComputedStyle(el).fontSize) || 0;
    if (!baseSize) return;

    // 0.8 is right for typical widths, but on very narrow viewports (e.g.
    // 315px) that floor gets hit on the first shrink, so a second overflow
    // has nowhere left to go and clips - allow a lower floor there instead.
    const MIN_SCALE = window.innerWidth <= NARROW_VIEWPORT_PX ? 0.6 : 0.8;
    const scale = available / el.scrollWidth;
    el.style.fontSize = `${Math.max(baseSize * scale, baseSize * MIN_SCALE)}px`;
  }, []);

  useEffect(() => {
    const el = spanRef.current;
    const pill = el?.parentElement;
    if (!el || !pill) return undefined;

    let raf1;
    let raf2;
    const scheduleFit = () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(fit);
      });
    };

    scheduleFit();

    const observer = new ResizeObserver(scheduleFit);
    observer.observe(pill);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      observer.disconnect();
    };
  }, [text, fit]);

  return spanRef;
}

function FitLabel({ text }) {
  const spanRef = useFitLabel(text);
  return <span ref={spanRef}>{text}</span>;
}

FitLabel.propTypes = {
  text: PropTypes.string.isRequired,
};

// The pill's own FLIP animation scales its box, which would stretch the
// glyphs. A nested layout child gets framer's inverse-scale correction, and
// fading masks whatever distortion is left while the width is still moving.
// useFitLabel shrinks this label's own font only if it actually overflows
// its pill (see useFitLabel above) - needed since pills can now shrink
// narrower than a label's natural text width (see getPillStyle).
function PillLabel({ isCollapsed, label }) {
  const spanRef = useFitLabel(label);
  return (
    <motion.span
      ref={spanRef}
      layout
      className={styles.pillLabel}
      initial={false}
      animate={{ opacity: isCollapsed ? 0 : 1 }}
      transition={
        isCollapsed
          ? { duration: 0.1, ease: "easeOut" }
          : { duration: 0.16, ease: "easeOut", delay: 0.12 }
      }
    >
      {label}
    </motion.span>
  );
}

PillLabel.propTypes = {
  isCollapsed: PropTypes.bool,
  label: PropTypes.string,
};

// Matches .subCategory's min-width/.subRow's gap in FilterButton.module.css.
const SUBCATEGORY_MIN_WIDTH = 64;
const SUBCATEGORY_GAP = 0.4 * 16;

// The API groups sub/third-level categories into rows via rowNumber (see
// groupByRowNumber) - the Category Builder design expects every item in a
// rowNumber group to sit on one line together, not wrap independently once
// its flex-basis no longer fits. This measures a row's own rendered width
// and decides whether every item in it can still fit on one line at-or-above
// SUBCATEGORY_MIN_WIDTH each; if so the row is forced flat (each pill takes
// an equal, calculated share of the row). Below that floor there simply
// isn't room for the row's real item count on this viewport, so it falls
// back to the row's normal flex-wrap/flex-basis breaking instead of
// clipping pills or forcing horizontal overflow.
function useRowFit(itemCount) {
  const rowRef = useRef(null);
  const [fitsRow, setFitsRow] = useState(true);

  const measure = useCallback(() => {
    const row = rowRef.current;
    if (!row || itemCount <= 0) return;

    const gapValue = parseFloat(window.getComputedStyle(row).columnGap);
    const gap = Number.isNaN(gapValue) ? SUBCATEGORY_GAP : gapValue;
    const available = row.clientWidth - gap * (itemCount - 1);
    const perItem = available / itemCount;
    setFitsRow(perItem >= SUBCATEGORY_MIN_WIDTH);
  }, [itemCount]);

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return undefined;

    let raf1;
    let raf2;
    const scheduleMeasure = () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(measure);
      });
    };

    scheduleMeasure();

    const observer = new ResizeObserver(scheduleMeasure);
    observer.observe(row);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      observer.disconnect();
    };
  }, [measure]);

  return { rowRef, fitsRow };
}

// Renders one rowNumber group of sub/third-level pills (see useRowFit above
// for the enforce-vs-wrap decision). When fitsRow is true, flex-wrap is
// switched to nowrap and each pill's flex-basis is calculated as an equal
// share of the row so the group's real item count (which varies row to row -
// e.g. 4 under Books' rowNumber 1, 2 for Read/Unread) lays out evenly
// instead of using a one-size-fits-all fixed basis.
function PillRow({
  items,
  className,
  isItemSelected,
  onSelectItem,
  getColorStyle,
}) {
  const { rowRef, fitsRow } = useRowFit(items.length);
  const gapCount = items.length - 1;

  return (
    <div
      ref={rowRef}
      className={className}
      style={fitsRow ? { flexWrap: "nowrap" } : undefined}
    >
      {items.map(({ title }) => {
        const isThisSelected = isItemSelected(title);
        const colorStyle = getColorStyle(title, isThisSelected);
        return (
          <motion.div
            key={title}
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className={styles.subCategory}
            style={{
              background: colorStyle.background,
              color: colorStyle.color,
              ...(fitsRow
                ? {
                    flex: `0 0 calc((100% - ${
                      gapCount * SUBCATEGORY_GAP
                    }px) / ${items.length})`,
                    minWidth: 0,
                  }
                : undefined),
            }}
            onClick={() => onSelectItem(title)}
          >
            <FitLabel text={title} />
          </motion.div>
        );
      })}
    </div>
  );
}

PillRow.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({ title: PropTypes.string }))
    .isRequired,
  className: PropTypes.string,
  isItemSelected: PropTypes.func.isRequired,
  onSelectItem: PropTypes.func.isRequired,
  getColorStyle: PropTypes.func.isRequired,
};

const FilterButton = ({
  onSubCategoryChange,
  onCategoryChange,
  apiCategories = [],
  initialCategory = "",
  initialSubCategory = "",
}) => {
  const [isSelected, setIsSelected] = useState("");
  const [isSubCategory, setIsSubCategory] = useState("");
  const [isThirdLevel, setIsThirdLevel] = useState("");

  const categories = useMemo(
    () => buildCategoriesFromAPI(apiCategories),
    [apiCategories]
  );

  const topLevelCategories = useMemo(
    () => Object.keys(categories),
    [categories]
  );

  // Categories load asynchronously, so wait until the category we need to
  // restore actually exists in the tree before restoring a selection carried
  // over from the URL. Note buildCategoriesFromAPI always returns a
  // `Favorites` entry even with no API data, so a non-empty tree is not on
  // its own proof that the real categories have arrived.
  const hasRestoredSelection = useRef(false);
  useEffect(() => {
    if (hasRestoredSelection.current) return;
    if (!initialCategory) return;

    const categoryValue = categories[initialCategory];
    if (categoryValue === undefined) return;

    hasRestoredSelection.current = true;
    setIsSelected(initialCategory);

    if (!initialSubCategory) return;

    const hasNestedStructure =
      typeof categoryValue === "object" &&
      !Array.isArray(categoryValue) &&
      categoryValue !== null;

    if (!hasNestedStructure) {
      // Flat category (e.g. Drinkware): the value is a leaf item.
      if (
        Array.isArray(categoryValue) &&
        categoryValue.includes(initialSubCategory)
      ) {
        setIsThirdLevel(initialSubCategory);
      }
      return;
    }

    if (
      Object.prototype.hasOwnProperty.call(categoryValue, initialSubCategory)
    ) {
      setIsSubCategory(initialSubCategory);
      return;
    }

    // Nested category and the value is a third-level leaf (e.g. Literature ->
    // Books -> Fiction). The middle level has to be restored too, otherwise
    // the third-level row never renders and the leaf selection stays hidden.
    const parentKey = Object.keys(categoryValue).find((key) =>
      (categoryValue[key]?.items || []).some(
        (gc) => gc.title === initialSubCategory
      )
    );

    if (parentKey) {
      setIsSubCategory(parentKey);
      setIsThirdLevel(initialSubCategory);
    }
  }, [categories, topLevelCategories, initialCategory, initialSubCategory]);

  const handleCategory = (category) => {
    setIsSelected(category);
    setIsSubCategory("");
    setIsThirdLevel("");
    onCategoryChange?.(category);
    onSubCategoryChange?.("");
  };

  const handleSubCategory = (subCategory) => {
    const categoryValue = categories[isSelected];
    const hasNestedStructure =
      typeof categoryValue === "object" &&
      !Array.isArray(categoryValue) &&
      categoryValue !== null;

    if (hasNestedStructure) {
      // For categories with nested structure (like Literature with Books, Info, etc.)
      // Toggle: if already selected, deselect it; otherwise select it
      const nextValue = subCategory === isSubCategory ? "" : subCategory;
      setIsSubCategory(nextValue);
      setIsThirdLevel("");
      onSubCategoryChange?.(nextValue);
    } else {
      // For flat categories, this is the final level
      const nextValue = subCategory === isThirdLevel ? "" : subCategory;
      setIsThirdLevel(nextValue);
      onSubCategoryChange?.(nextValue);
    }
  };

  const handleThirdLevel = (thirdLevel) => {
    const nextValue = thirdLevel === isThirdLevel ? "" : thirdLevel;
    setIsThirdLevel(nextValue);
    onSubCategoryChange?.(nextValue);
  };

  const handleBackButton = () => {
    if (isThirdLevel) {
      setIsThirdLevel("");
    } else if (isSubCategory) {
      setIsSubCategory("");
      onSubCategoryChange?.("");
    } else {
      setIsSelected("");
      setIsSubCategory("");
      setIsThirdLevel("");
      onCategoryChange?.("");
      onSubCategoryChange?.("");
    }
  };

  const getSubItems = () => {
    const categoryValue = categories[isSelected];

    if (!categoryValue) return [];

    // If it's an array, return as-is (flat top-level category with no
    // middle-level structure at all - normalize to the same {title,
    // rowNumber} shape as the nested case below).
    if (Array.isArray(categoryValue)) {
      return categoryValue.map((title) => ({ title, rowNumber: 1 }));
    }

    // If it's an object (nested structure like Literature)
    if (typeof categoryValue === "object" && categoryValue !== null) {
      if (!isSubCategory) {
        // Return the keys (Books, Info, Stationary, Bundles, etc.)
        return Object.keys(categoryValue).map((title) => ({
          title,
          rowNumber: categoryValue[title].rowNumber,
        }));
      }
      // Return the items under the selected sub-category
      return categoryValue[isSubCategory]?.items || [];
    }

    return [];
  };

  const getMiddleLevelItems = () => {
    // Always return the keys for middle-level categories (Books, Info, etc.)
    const categoryValue = categories[isSelected];

    if (!categoryValue) return [];

    if (typeof categoryValue === "object" && !Array.isArray(categoryValue)) {
      return Object.keys(categoryValue).map((title) => ({
        title,
        rowNumber: categoryValue[title].rowNumber,
      }));
    }

    return [];
  };

  // The top-level pill always displays its own top-level category name once
  // selected, regardless of how deep the user has drilled into subcategories
  // - it must never swap to showing a child/grandchild name instead.
  const getCurrentHeader = () => isSelected;
  const mainRowCollapsed = isSelected === "Favorites";
  const favoritesRowCollapsed = isSelected !== "" && isSelected !== "Favorites";

  // Both branches must declare the same keys: framer-motion writes styles
  // imperatively to the node and will not clear properties that disappear
  // between renders, so every collapsed property needs an explicit reset.
  //
  // Spacing between pills is driven by marginLeft rather than the row's
  // flex `gap`, because collapsed pills still occupy a flex slot: with
  // `gap`, each invisible collapsed pill ahead of the selected one added
  // its own gap, so the arrow-to-pill spacing grew with the selected
  // category's position in the list. Zeroing marginLeft on collapsed pills
  // keeps that spacing constant regardless of how many precede it. Visible
  // pills always get the same 0.4rem margin - the pillGroup wrapper's
  // matching negative margin-left absorbs it at the start of every row
  // (including wrapped lines), so it never leaves a stray left offset.
  // flexBasis/minWidth/padding are intentionally left to the CSS
  // (.categoryPill) rather than hardcoded here, so the responsive
  // container-query sizing that lets 4 pills fit a row on modern phones
  // (see FilterButton.module.css) actually applies. overflow is switched to
  // "hidden" for the expanded state (was "visible") so flex-shrink can
  // shrink the box below its text's natural width instead of being floored
  // by min-content - PillLabel's fit-to-width measurement then shrinks the
  // font just enough to avoid clipping, rather than the box refusing to
  // shrink and forcing an early wrap.
  const getPillStyle = (isCollapsed) =>
    isCollapsed
      ? {
          flexGrow: 0,
          flexBasis: 0,
          minWidth: 0,
          height: 28,
          padding: "4px 0",
          margin: 0,
          opacity: 1,
          overflow: "hidden",
          border: "none",
          pointerEvents: "none",
        }
      : {
          flexGrow: 1,
          height: 28,
          margin: "0 0 0 0.4rem",
          opacity: 1,
          overflow: "hidden",
          border: "1px solid",
          pointerEvents: "auto",
        };

  if (topLevelCategories.length === 0) {
    return null;
  }

  return (
    <>
      <div
        className={styles.row}
        style={
          mainRowCollapsed
            ? { height: 0, margin: 0, overflow: "hidden" }
            : undefined
        }
      >
        {isSelected !== "" && isSelected !== "Favorites" && (
          <button
            type="button"
            className={styles.backButton}
            onClick={handleBackButton}
            aria-label="Go back"
          >
            <img src={path} alt="" />
          </button>
        )}
        <div className={styles.pillGroup}>
          {topLevelCategories
            .filter((cat) => cat !== "Favorites")
            .map((category) => {
              const isThisSelected = isSelected === category;
              const isCollapsed = isSelected !== "" && !isThisSelected;
              const record = findCategoryRecord(apiCategories, category, null);
              const colorStyle = getCategoryColorStyle(record, isThisSelected);
              return (
                <motion.div
                  key={category}
                  layout
                  transition={{ duration: 0.22, ease: "easeOut" }}
                  className={styles.categoryPill}
                  style={{
                    ...getPillStyle(isCollapsed),
                    ...(isCollapsed
                      ? {}
                      : {
                          background: colorStyle.background,
                          color: colorStyle.color,
                        }),
                  }}
                  aria-hidden={isCollapsed || undefined}
                  onClick={() => {
                    if (isSelected === "") handleCategory(category);
                    else if (isThisSelected) return;
                  }}
                >
                  <PillLabel
                    isCollapsed={isCollapsed}
                    label={isThisSelected ? getCurrentHeader() : category}
                  />
                </motion.div>
              );
            })}
        </div>
      </div>
      {topLevelCategories.includes("Favorites") && (
        <div
          className={styles.row}
          style={
            favoritesRowCollapsed
              ? { height: 0, margin: 0, overflow: "hidden" }
              : undefined
          }
        >
          {isSelected === "Favorites" && (
            <button
              type="button"
              className={styles.backButton}
              onClick={handleBackButton}
              aria-label="Go back"
            >
              <img src={path} alt="" />
            </button>
          )}
          {(() => {
            const isThisSelected = isSelected === "Favorites";
            const isCollapsed = isSelected !== "" && !isThisSelected;
            // Favorites is always the only pill in its row, so there's no
            // wrap-line to align with the pillGroup trick above - just zero
            // its own margin when it's the flush-left starting pill.
            const suppressLeftMargin = isSelected === "";
            const record = findCategoryRecord(apiCategories, "Favorites", null);
            const colorStyle = getCategoryColorStyle(record, isThisSelected);
            return (
              <motion.div
                layout
                transition={{ duration: 0.22, ease: "easeOut" }}
                className={styles.categoryPill}
                style={{
                  ...getPillStyle(isCollapsed),
                  ...(isCollapsed
                    ? {}
                    : {
                        background: colorStyle.background,
                        color: colorStyle.color,
                      }),
                  margin: isCollapsed
                    ? 0
                    : `0 0 0 ${suppressLeftMargin ? 0 : "0.4rem"}`,
                }}
                aria-hidden={isCollapsed || undefined}
                onClick={() => {
                  if (isSelected === "") handleCategory("Favorites");
                }}
              >
                <PillLabel
                  isCollapsed={isCollapsed}
                  label={isThisSelected ? getCurrentHeader() : "Favorites"}
                />
              </motion.div>
            );
          })()}
        </div>
      )}

      {isSelected && (
        <>
          {/* Show middle-level items if category has nested structure */}
          {(() => {
            const categoryValue = categories[isSelected];
            const hasNestedStructure =
              typeof categoryValue === "object" &&
              !Array.isArray(categoryValue) &&
              categoryValue !== null;

            if (hasNestedStructure) {
              // Always show the first level of nested items (Books, Info, etc. for Literature)
              // Highlight the selected one if any. Grouped by rowNumber so
              // items the API places on different rows (e.g. a rowNumber-2
              // group) render as separate rows instead of one flat wrap.
              const topRecord = findCategoryRecord(
                apiCategories,
                isSelected,
                null
              );
              const rows = groupByRowNumber(getMiddleLevelItems());
              return rows.map(({ rowNumber, items }) => (
                <PillRow
                  key={rowNumber}
                  items={items}
                  className={`${styles.subRow} ${styles.literatureSubRow}`}
                  isItemSelected={(title) => title === isSubCategory}
                  onSelectItem={handleSubCategory}
                  getColorStyle={(title, isThisSelected) => {
                    const record = findCategoryRecord(
                      apiCategories,
                      title,
                      topRecord?.categoryId ?? null
                    );
                    return getCategoryColorStyle(record, isThisSelected);
                  }}
                />
              ));
            }
            return null;
          })()}

          {/* Show third level items (Books subcategories or flat category items) */}
          {(() => {
            const categoryValue = categories[isSelected];
            const hasNestedStructure =
              typeof categoryValue === "object" &&
              !Array.isArray(categoryValue) &&
              categoryValue !== null;

            if (
              (hasNestedStructure && isSubCategory) ||
              (!hasNestedStructure && isSelected !== "Favorites")
            ) {
              const topRecord = findCategoryRecord(
                apiCategories,
                isSelected,
                null
              );
              const parentId = hasNestedStructure
                ? findCategoryRecord(
                    apiCategories,
                    isSubCategory,
                    topRecord?.categoryId ?? null
                  )?.categoryId ?? null
                : topRecord?.categoryId ?? null;
              const rows = groupByRowNumber(getSubItems());
              return rows.map(({ rowNumber, items }) => (
                <PillRow
                  key={rowNumber}
                  items={items}
                  className={styles.subRow}
                  isItemSelected={(title) => title === isThirdLevel}
                  onSelectItem={handleThirdLevel}
                  getColorStyle={(title, isThisSelected) => {
                    const record = findCategoryRecord(
                      apiCategories,
                      title,
                      parentId
                    );
                    return getCategoryColorStyle(record, isThisSelected);
                  }}
                />
              ));
            }
            return null;
          })()}
        </>
      )}
    </>
  );
};

FilterButton.propTypes = {
  onSubCategoryChange: PropTypes.func,
  onCategoryChange: PropTypes.func,
  apiCategories: PropTypes.arrayOf(
    PropTypes.shape({
      categoryId: PropTypes.number,
      title: PropTypes.string,
      parentCategoryId: PropTypes.number,
      sortOrder: PropTypes.number,
      backgroundColor: PropTypes.string,
      textColor: PropTypes.string,
      colorFill: PropTypes.bool,
    })
  ),
  initialCategory: PropTypes.string,
  initialSubCategory: PropTypes.string,
};

export default FilterButton;
